import { createJSONStorage } from "zustand/middleware";

/** Storage that safely parses JSON - returns null for HTML/corrupt values to prevent SyntaxError. */
const createSafeJSONStorage = () => {
  const base = createSafeStorage();
  return {
    getItem: (name: string): string | null => {
      const value = base.getItem(name);
      if (!value) return null;
      const trimmed = value.trim();
      // Reject HTML/corrupt - prevents "Unexpected token '<', \"<!DOCTYPE \"..."
      if (!trimmed || trimmed.startsWith("<") || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) {
        return null;
      }
      try {
        JSON.parse(value); // validate before returning to createJSONStorage
        return value;
      } catch {
        return null;
      }
    },
    setItem: base.setItem.bind(base),
    removeItem: base.removeItem.bind(base),
  };
};

const noopStorage: Storage = {
  get length() {
    return 0;
  },
  clear: () => undefined,
  getItem: () => null,
  key: () => null,
  removeItem: () => undefined,
  setItem: () => undefined,
};

/**
 * Enhanced localStorage wrapper that handles mobile browser quirks and quota issues
 * CRITICAL: This ensures auth state persists across window/app closes on both web and mobile
 */
const createSafeStorage = (): Storage => {
  if (typeof window === "undefined") {
    return noopStorage;
  }

  return {
    get length() {
      try {
        return window.localStorage.length;
      } catch {
        return 0;
      }
    },
    clear: () => {
      try {
        // CRITICAL: Don't clear Supabase auth tokens when clearing storage
        // Only clear non-auth keys
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && !key.includes("supabase") && !key.includes("sb-") && !key.includes("auth-token")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => window.localStorage.removeItem(key));
      } catch (error) {
        console.warn("[Safe Storage] Error clearing storage:", error);
      }
    },
    getItem: (key: string) => {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        console.warn(`[Safe Storage] Error reading key ${key}:`, error);
        return null;
      }
    },
    key: (index: number) => {
      try {
        return window.localStorage.key(index);
      } catch {
        return null;
      }
    },
    removeItem: (key: string) => {
      try {
        // CRITICAL: Protect Supabase auth tokens from accidental removal
        if (key.includes("supabase") || key.includes("sb-") || key.includes("auth-token")) {
          console.warn(`[Safe Storage] Attempted to remove protected auth key: ${key}`);
          return;
        }
        window.localStorage.removeItem(key);
      } catch (error) {
        console.warn(`[Safe Storage] Error removing key ${key}:`, error);
      }
    },
    setItem: (key: string, value: string) => {
      const isAuthKey =
        key.includes("supabase") ||
        key.includes("sb-") ||
        key.includes("auth-token") ||
        key.includes("growthsync-auth");

      const trySet = () => {
        window.localStorage.setItem(key, value);
      };

      try {
        trySet();
      } catch (error) {
        // Handle quota exceeded errors gracefully
        if (error instanceof DOMException && error.name === "QuotaExceededError") {
          console.warn("[Safe Storage] localStorage quota exceeded, attempting cleanup");
          const keysToRemove: string[] = [];
          for (let i = 0; i < window.localStorage.length; i++) {
            const storageKey = window.localStorage.key(i);
            if (
              storageKey &&
              !storageKey.includes("supabase") &&
              !storageKey.includes("sb-") &&
              !storageKey.includes("auth-token") &&
              !storageKey.includes("growthsync-auth")
            ) {
              keysToRemove.push(storageKey);
            }
          }
          // Remove ALL non-auth keys (not just 10) to free maximum space
          keysToRemove.forEach((k) => window.localStorage.removeItem(k));
          try {
            trySet();
          } catch (retryError) {
            if (isAuthKey) {
              console.error(
                "[Safe Storage] Failed to free space, cannot persist auth state:",
                retryError
              );
            } else {
              console.warn(
                `[Safe Storage] Quota exceeded, could not persist ${key}. Data will reload from source.`,
                retryError
              );
            }
            // Never throw - allow app to continue; non-auth data can be re-fetched
          }
        } else {
          console.warn(`[Safe Storage] Error setting key ${key}:`, error);
        }
      }
    },
  };
};

export const createPersistStorage = () =>
  createJSONStorage(() => createSafeJSONStorage());


